import React, { useState } from 'react';
import { CheckedState } from '@radix-ui/react-checkbox';
import { ValueFormField } from '@sitecore-jss/sitecore-jss-forms';
import { FieldChangeCallback } from '@sitecore-jss/sitecore-jss-react-forms';
import { ListFieldProps } from '@sitecore-jss/sitecore-jss-react-forms/types/FieldProps';
import classNames from 'classnames';

import { JabraCheckbox } from '@gn-audio/jss-components/components/JabraCheckbox/JabraCheckbox';

import { getCheckboxClassNames } from 'components/FieldTemplates/Checkbox';
import { RichLabelProps } from 'components/Shared/JabraLabel';

import { longDescriptionColor } from '../../contexts/FormTheme/formThemes';
import { CheckboxListViewModel } from '../../types/checkboxList';

const CheckboxList: React.FC<ListFieldProps<CheckboxListViewModel>> = (props) => {
  const {
    field,
    field: {
      model: { items, title, richTitle },
    },
    value,
    onChange,
    errors,
    tracker,
  } = props;
  const { displayLabel, labelCssClass } = field.model;

  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const hasAnyError = !!errors?.length;

  const formDesignerClasses = props.field.model.cssClass?.trim();

  const FieldValidationErrors = props.fieldValidationErrorsComponent as React.FC;
  const Label = props.labelComponent as React.FC<RichLabelProps>;

  const hasLabel = !!Label && (title || richTitle);

  return (
    <>
      {hasLabel && <Label {...props} component="legend" />}
      <fieldset className={formDesignerClasses} aria-label={title}>
        {items.map((item, index) => {
          const hasFocus = focusedIndex === index;
          const id = field.valueField.id + index;
          return (
            <div key={item.itemId} className="flex items-center">
              <JabraCheckbox
                id={id}
                name={field.valueField.name}
                value={item.value}
                checked={value.some((v) => v === item.value)}
                className={getCheckboxClassNames(hasFocus, hasAnyError)}
                onCheckedChange={(isChecked: CheckedState) =>
                  handleOnChange(field, value, item.value, !!isChecked.valueOf(), onChange)
                }
                onFocus={() => {
                  setFocusedIndex(index);
                  tracker.onFocusField(field, item.value);
                }}
                onBlur={() => {
                  setFocusedIndex(null);
                  tracker.onBlurField(field, item.value, errors);
                }}
              />
              <label htmlFor={id} className="cursor-default">
                {item.text}
              </label>
            </div>
          );
        })}
      </fieldset>

      {displayLabel && (
        <p
          className={classNames('mt-2 cursor-default', longDescriptionColor, labelCssClass)}
          dangerouslySetInnerHTML={{ __html: displayLabel || '' }}
        />
      )}

      {!!FieldValidationErrors && <FieldValidationErrors {...props} />}
    </>
  );
};

function handleOnChange(
  field: ValueFormField,
  originalFieldValue: string[],
  changedElement: string,
  checked: boolean,
  callback: FieldChangeCallback
) {
  let value = originalFieldValue;

  if (checked) {
    value.push(changedElement);
  } else {
    value = value.filter((v) => v !== changedElement);
  }

  callback(field.valueField.name, value, true, []);
}

export default CheckboxList;
